/**
 * 	Terra's main page, contains the Layout, Header, Footer, and Side Menu.
 *  Any other content will be available in the <Outlet/> element, which renders
 * 	components based on the current endpoint in the URL
 */

import { Dropdown, Image, Layout, Space, Spin, theme, Typography } from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Outlet } from "react-router";
import file from "../../package.json";
import terraLogoFooterDark from "../assets/image/TerraLogo_Footer_Darkmode.svg";
import terraLogoFooterLight from "../assets/image/TerraLogo_Footer_Lightmode.svg";
import elementalLoadingLogo from "../assets/image/elemental 'e'.svg";
import elementalLoadingOutline from "../assets/image/outline 'e'.svg";
import AccountMenu, { UserAvatar } from "../components/AccountMenu";
import BreadcrumbComponent from "../components/BreadcrumbComponent";
import MyTasksNavigationButton from "../components/Button/MyTasksNavigationButton";
import ContextMenu from "../components/ContextMenu";
import LeaveWithChangesModal from "../components/LeaveWithChangesModal";
import NotificationComponent from "../components/NotificationComponent";
import WhatsNewDrawer from "../components/WhatsNewDrawer";
import { MainContext, useThemeContext } from "../contexts/context";
import useInitOperation from "../hooks/useInitOperation";
import useInitPhase from "../hooks/useInitPhase";
import useInitProject from "../hooks/useInitProject";
import useInitTask from "../hooks/useInitTask";
import useInitWell from "../hooks/useInitWell";
import MainMenu from "../services/MainMenu";
import { sendErrorNotification } from "../services/api-server/base";
import { getBusinessUnitList } from "../services/api-server/business_unit";
import { getCompanyList } from "../services/api-server/company";
import { getDepartmentsList } from "../services/api-server/departments";
import { getDisciplineList } from "../services/api-server/discipline";
import { getTenantSpecificUsers } from "../services/api-server/graphql";
import { getIncidentReportList } from "../services/api-server/incident_report";
import { getIncidentTypeList } from "../services/api-server/incident_type";
import { getLessonLearnedClassificationList } from "../services/api-server/lesson_learned_classification";
import { getLessonLearnedTypeList } from "../services/api-server/lesson_learned_type";
import { getOperatingOfficeList } from "../services/api-server/operating_office";
import { getOperationTypeList } from "../services/api-server/operation_type";
import { getOperations } from "../services/api-server/operations";
import { getOriginList } from "../services/api-server/origin";
import { getPhases } from "../services/api-server/phases";
import { getProjectTagList } from "../services/api-server/project_tags";
import { getProjects } from "../services/api-server/projects";
import { getWellPurposeTypeList } from "../services/api-server/purpose_types";
import { getWellPurposeList } from "../services/api-server/purposes";
import { getRegionList } from "../services/api-server/region";
import { getRigList } from "../services/api-server/rig";
import { getSectionList } from "../services/api-server/section";
import { getSoftwareList } from "../services/api-server/software";
import { getWellStatusList } from "../services/api-server/status";
import { getWellStatusTypeList } from "../services/api-server/status_types";
import { getTagGroupList } from "../services/api-server/tag_group";
import { getTasks } from "../services/api-server/tasks";
import { getWellProgrammeTemplateList } from "../services/api-server/well_programme_template";
import { getWells } from "../services/api-server/wells";
import {
	getRolePermissionsList,
	getUserRole,
} from "../services/api-server/roles_permissions";
import { useTenantStore } from "../stores/tenantStore";
import { usePermissionStore } from "../stores/permissionStore";
import Emitter from "../utils/emitter";
import { pickInitialTenant } from "../utils/initialTenant";
import { socket } from "../utils/socket";
import NoAccess from "./NoAccess";

/** Lookup data an examiner-only session may load (mirrors the server allowlist). */
const EXAMINER_DATA_KEYS = new Set([
	"strategic_business_unit",
	"tag_group",
	"project_tags",
	"operating_office",
	"projects",
	"region",
	"company",
]);

const { Header, Content, Footer } = Layout;

export const HomeContext = React.createContext({});

const Home = (props: any) => {
	const formUpdatingRef = useRef<any>([]);

	const loadingRef = useRef<any>({});

	const setTenant = useTenantStore((state) => state.setTenant);
	const { token } = theme.useToken();
	const isPermissionsInitialized = usePermissionStore(
		(state) => state.isInitialized
	);
	const isExaminerOnly = usePermissionStore(
		(state) => !!state.accessProfile?.restricted
	);

	const [allData, setAllData] = useState<any>(null);
	const [isLoading, setLoading] = useState<any>(false);
	const [selectedKey, setSelectedKey] = useState<any>([]);
	const [isCollapsed, setCollapsed] = useState<boolean>(false);
	const [currentTenantData, setCurrentTenantData] = useState<any>(null);
	const [currentTenantKey, setCurrentTenantKey] = useState<any>(null);
	const [currentProject, setCurrentProject] = useState<any>(null);
	const [currentWell, setCurrentWell] = useState<any>({});
	const [currentOperation, setCurrentOperation] = useState<any>({});
	const [currentPhase, setCurrentPhase] = useState<any>({});
	const [currentTask, setCurrentTask] = useState<any>({});

	// Data states
	const [rolePermissions, setRolePermissions] = useState<any[]>([]);
	const [userRole, setUserRole] = useState<any>(null);

	const tenantData = props?.tenants;
	const [pageChange, setPageChange] = useState<boolean>(false);
	const [popconfirmDialogOpen, setPopconfirmDialogOpen] =
		useState<boolean>(false);
	const [tenantDropdownOpen, setTenantDropdownOpen] = useState<boolean>(false);

	const [errorBoundaryKey, incrementErrorBoundaryKey] = useState(0);

	const [contextMenuPosition, setContextMenuPosition] = useState<{
		mouseX: number;
		mouseY: number;
	} | null>(null);
	const [contextMenuInfo, setContextMenuInfo] = useState<any>(null);
	const [isWhatsNewOpen, setWhatsNewOpen] = useState<boolean>(false);
	const { mode } = useThemeContext();

	// Initialize project from url (For when use link to open a project)
	useInitProject(currentTenantKey);
	useInitOperation(currentTenantKey);
	useInitPhase(currentTenantKey);
	useInitTask(currentTenantKey);
	useInitWell(currentTenantKey);

	useEffect(() => {
		// Wait for the access profile so examiner-only sessions never request
		// data the server will reject.
		if (!currentTenantKey || !currentTenantData || !isPermissionsInitialized)
			return;

		const fetchAllData = async () => {
			try {
				let allData: any = {};

				const allApiCalls = [
					{
						key: "strategic_business_unit",
						fn: () => getBusinessUnitList(currentTenantKey),
					},
					{ key: "tag_group", fn: () => getTagGroupList(currentTenantKey) },
					{
						key: "project_tags",
						fn: () => getProjectTagList(currentTenantKey),
					},
					{ key: "operation", fn: () => getOperations(currentTenantKey, {}) },
					{
						key: "operating_office",
						fn: () => getOperatingOfficeList(currentTenantKey),
					},
					{ key: "projects", fn: () => getProjects(currentTenantKey) },
					{ key: "region", fn: () => getRegionList(currentTenantKey) },
					{
						key: "company",
						fn: () => getCompanyList(currentTenantKey, [], true),
					},
					{
						key: "users",
						fn: () => getTenantSpecificUsers(currentTenantData.uuid),
					},
					{ key: "well", fn: () => getWells(currentTenantKey, {}) },
					{ key: "well_purpose", fn: () => getWellPurposeList() },
					{ key: "well_purpose_type", fn: () => getWellPurposeTypeList() },
					{ key: "well_status", fn: () => getWellStatusList() },
					{ key: "well_status_type", fn: () => getWellStatusTypeList() },
					{ key: "operation_type", fn: () => getOperationTypeList() },
					{
						key: "well_programme_template",
						fn: () => getWellProgrammeTemplateList(currentTenantKey),
					},
					{ key: "software", fn: () => getSoftwareList(currentTenantKey) },
					{
						key: "incident_type",
						fn: () => getIncidentTypeList(currentTenantKey),
					},
					{ key: "incident_report", fn: () => getIncidentReportList() },
					{ key: "rig", fn: () => getRigList() },
					{ key: "discipline", fn: () => getDisciplineList() },
					{ key: "origin", fn: () => getOriginList() },
					{ key: "section", fn: () => getSectionList() },
					{ key: "lesson_learned_type", fn: () => getLessonLearnedTypeList() },
					{
						key: "lesson_learned_classification",
						fn: () => getLessonLearnedClassificationList(),
					},
					{ key: "phase", fn: () => getPhases(currentTenantKey) },
					{ key: "task", fn: () => getTasks(currentTenantKey, {}) },
					{ key: "department", fn: () => getDepartmentsList(currentTenantKey) },
				];

				const apiCalls = isExaminerOnly
					? allApiCalls.filter(({ key }) => EXAMINER_DATA_KEYS.has(key))
					: allApiCalls;

				await Promise.all(
					apiCalls.map(async ({ key, fn }) => {
						try {
							const result = await fn();
							allData[key] = result || [];
						} catch (error) {
							// One failing lookup must not leave the app without any data.
							console.warn(`Failed to load ${key}:`, error);
							allData[key] = [];
						}
					})
				);
				setAllData(allData);
			} catch (error) {
				console.error(error);
			}
		};

		void fetchAllData();
	}, [
		currentTenantData,
		currentTenantKey,
		isPermissionsInitialized,
		isExaminerOnly,
	]);

	// Fetch role permissions and user role when tenant changes
	useEffect(() => {
		if (!currentTenantKey) return;

		const fetchRoleData = async () => {
			try {
				// Fetch role permissions - wrap in try-catch individually to prevent one failure from breaking the other
				let permissionsData: any = [];
				try {
					permissionsData = await getRolePermissionsList(currentTenantKey);
				} catch (permError) {
					console.warn("Failed to fetch role permissions:", permError);
					permissionsData = [];
				}
				setRolePermissions(permissionsData || []);

				// Fetch user role if user info is available
				if (props.userInfo?.user?.email) {
					let userRoleData: any = null;
					try {
						userRoleData = await getUserRole(
							currentTenantKey,
							props.userInfo.user.email
						);
					} catch (roleError) {
						console.warn("Failed to fetch user role:", roleError);
						userRoleData = null;
					}
					setUserRole(userRoleData || null);
				}
			} catch (error) {
				// This outer catch is a safety net
				console.error("Unexpected error in fetchRoleData:", error);
				setRolePermissions([]);
				setUserRole(null);
			}
		};

		// Use setTimeout to ensure this doesn't block the main render thread
		const timeoutId = setTimeout(() => {
			void fetchRoleData();
		}, 0);

		return () => clearTimeout(timeoutId);
	}, [currentTenantKey, props.userInfo?.user?.email]);

	const handleErrorFallback = () => {
		return (
			<NoAccess
				text={"An Error has occurred, please try again later."}
				setErrorBoundaryKey={() => {
					console.log("Increment boundary key");
					incrementErrorBoundaryKey(errorBoundaryKey + 1);
				}}
			/>
		);
	};

	const logError = (error: any) => {
		if (import.meta.env.VITE_NODE_ENV === "production") {
			error.location = window.location.pathname;
			error.level = "component";
			let JSONerror = JSON.stringify(error, Object.getOwnPropertyNames(error));
			sendErrorNotification(JSONerror);
		}
	};

	useEffect(() => {
		Emitter.on("menuChange", (menuProps: any) => {
			if (
				localStorage.getItem("selectedFilters") &&
				menuProps.container !== "projectSettings"
			) {
				localStorage.removeItem("selectedFilters");
			}
		});

		Emitter.on("loading", ({ name, isLoading }: any) => {
			const loadingReferences: any = loadingRef.current;

			// track loading state for that particular reference
			if (name) {
				loadingReferences[name] = isLoading;
			}

			// check if all references are not loading
			const someLoading = Object.values(loadingReferences).some(Boolean);

			setLoading(someLoading);
		});

		// refetch tenant data
		socket.on(`authorized_modules`, (payload: any) => {
			console.log("authorized modules hit", payload);
			setCurrentTenantData({ ...payload });
			setTenant({ ...payload });
		});
	}, []);

	useEffect(() => {
		if (tenantData) {
			// App has already stored the tenant to start in; follow it.
			const chosen = pickInitialTenant(tenantData, [
				localStorage.getItem("currentTenantKey"),
			]);
			if (chosen) {
				setCurrentTenantKey(chosen.value);
				setCurrentTenantData({ ...chosen });
				setTenant({ ...chosen });
				localStorage.setItem("currentTenantKey", chosen.value);
			}
		}
	}, [props.tenants]);

	// Initialize permission store when tenant or user changes
	useEffect(() => {
		if (currentTenantKey && currentTenantData && props.userInfo?.user?.email) {
			void usePermissionStore
				.getState()
				.initializePermissions(currentTenantKey, currentTenantData);
		}
	}, [currentTenantKey, currentTenantData, props.userInfo?.user?.email]);

	useEffect(() => {
		// Each page's route is mounted at its menu item's last path segment, so
		// the URL's first segment names the item. Later segments, such as a Help
		// Centre topic key, can repeat another item's path.
		const firstSegment = props.currentLocation.split("/")[1];
		const menuItem = props.menu.find(
			(element: any) => element?.item?.to.split("/").pop() === firstSegment
		);
		if (menuItem) {
			incrementErrorBoundaryKey(errorBoundaryKey + 1);
			setSelectedKey(menuItem.item.key);
		}
		handleFormUpdating(false);
	}, [props.currentLocation]);

	const onCollapse = (collapseState: any) => {
		setCollapsed(collapseState);
	};

	const handleFormUpdating = (bool: any, key: any = null) => {
		if (key === null) {
			key = "new";
		}
		setPageChange(false);
		if (bool === true && key) {
			formUpdatingRef.current[key] = true;
		} else if (bool === false && key) {
			delete formUpdatingRef.current[key];
		}
	};

	const handlePageChange = () => {
		// Inside the function, we create a new Promise.
		// The resolve function is called when the operation completes successfully, and the reject function is called if something goes wrong or conditions aren't met.
		return new Promise((resolve, reject) => {
			// This updates a state (presumably a useState hook) named pageChange to true. This likely signals that a page change is happening.
			setPageChange(true);
			// This calculates the length of the object stored in formUpdatingRef.current (likely a reference to track some form's state). It counts how many keys (properties) the object has, which helps determine if any forms are being updated.
			let length = Object.keys(formUpdatingRef.current).length;

			if (length > 0) {
				// This sets up an event listener that waits for the "leave" event. When this event is emitted, the following actions are performed:
				Emitter.on("leave", () => {
					// This resolves the promise, indicating that the page change was successful.
					resolve(true);
					// Clears the formUpdatingRef.current object to reset form-related state.
					formUpdatingRef.current = {};
					// Updates the pageChange state back to false to indicate that the page change process is completed.
					setPageChange(false);
					// These remove the event listeners for "leave" and "stay" so they don't trigger multiple times.
					Emitter.remove("leave");
					Emitter.remove("stay");
					// Clears the tabKey from session storage.
					sessionStorage.removeItem("tabKey");
				});
				// This sets up an event listener for the "stay" event. When this event is emitted, the following actions happen:
				Emitter.on("stay", () => {
					// Rejects the promise, indicating that the page change should not happen (the user is staying on the current page).
					reject(false);
					// Updates the pageChange state back to false.
					setPageChange(false);
					// Removes the event listeners for "leave" and "stay", so they are no longer active.
					Emitter.remove("leave");
					Emitter.remove("stay");
				});
				// This block executes if length <= 0, meaning there are no forms currently being updated.
			} else {
				// Clears the tabKey from session storage, which is related to the current tab (possibly tracking which tab was selected).
				sessionStorage.removeItem("tabKey");
				// Resolves the promise, indicating that the page change is allowed to proceed.
				resolve(true);
				// Resets the formUpdatingRef.current object.
				formUpdatingRef.current = {};
				// Updates the pageChange state back to false.
				setPageChange(false);
			}
		});
	};

	// Read from the tenant list, not currentTenantData: the authorized_modules
	// broadcast overwrites that with whichever tenant was last edited.
	const currentTenant = useMemo(
		() => tenantData?.find((tenant: any) => tenant?.value === currentTenantKey),
		[tenantData, currentTenantKey]
	);
	const tenantName = currentTenant?.name || currentTenant?.displayName;

	const renderAccountMenu = () => (
		<AccountMenu
			user={props.userInfo.user}
			tenants={tenantData}
			currentTenantKey={currentTenantKey}
			onConfirmOpenChange={setPopconfirmDialogOpen}
		/>
	);

	/**
	 * Memoize context values to prevent unnecessary re-renders
	 */
	// Memoize all tenants
	const allTenants = useMemo(
		() => props.tenants.map((t: any) => t.role_key),
		[props.tenants]
	);
	// Memoize context value
	const contextValue = useMemo(
		() => ({
			isLoading,
			allData,
			userRole,
			rolePermissionsData: rolePermissions,
			tenantData,
			currentTenantData,
			allTenants,
			currentTenantKey,
			currentProject,
			currentWell,
			currentOperation,
			currentPhase,
			currentTask,
			userInfo: props.userInfo,
			setUserRole,
			setCurrentProject,
			setCurrentWell,
			setCurrentOperation,
			setCurrentPhase,
			setCurrentTask,
			handleFormUpdating,
			handlePageChange,
			setContextMenuPosition,
			setContextMenuInfo,
			setWhatsNewOpen,
		}),
		[
			isLoading,
			allData,
			userRole,
			rolePermissions,
			tenantData,
			currentTenantData,
			allTenants,
			currentTenantKey,
			currentProject,
			currentWell,
			currentOperation,
			currentPhase,
			currentTask,
			props.userInfo,
			handleFormUpdating,
			handlePageChange,
			setContextMenuPosition,
			setContextMenuInfo,
			setWhatsNewOpen,
		]
	);

	return (
		<MainContext.Consumer>
			{({ menuOption }) => {
				return (
					<HomeContext.Provider value={contextValue}>
						<Layout className="site-whole">
							<MainMenu
								collapsed={isCollapsed}
								onCollapse={(collapseState: boolean) => {
									onCollapse(collapseState);
								}}
								selectedKey={selectedKey}
								option={menuOption}
								roles={props.userInfo.userRole}
							></MainMenu>
							<Layout className="site-layout">
								<Header className="site-header">
									<BreadcrumbComponent />
									<div className="flex items-center gap-3">
										<MyTasksNavigationButton />
										<div>
											{props?.tenants?.length > 0 && <NotificationComponent />}
										</div>
										<div>
											{/* Tenants Dropdown */}
											<div>
												<Dropdown
													open={popconfirmDialogOpen || tenantDropdownOpen}
													rootClassName="account-menu-dropdown"
													popupRender={renderAccountMenu}
													placement="bottomRight"
													trigger={["hover"]}
													onOpenChange={(boolean: any) => {
														setTenantDropdownOpen(boolean);
													}}
												>
													{tenantName ? (
														// The tenant stays in view; switching stays in this menu.
														<div
															className="header-account-pill"
															// Colours from the theme, to match the icon buttons
															// beside it. Variables, so the stylesheet can still
															// apply hover and open states.
															style={
																{
																	"--header-pill-bg": token.colorBgContainer,
																	"--header-pill-border": token.colorBorder,
																	"--header-pill-border-active":
																		token.colorPrimaryHover,
																} as React.CSSProperties
															}
														>
															<Typography.Text
																className="header-account-pill-name"
																// No tooltip: it would compete with the menu, which
																// lists the full tenant name.
																ellipsis
															>
																{tenantName}
															</Typography.Text>
															<UserAvatar
																user={props.userInfo.user}
																size={24}
															/>
														</div>
													) : (
														// A DOM element: the dropdown attaches its hover
														// handlers to this child, and UserAvatar drops them.
														<div>
															<UserAvatar user={props.userInfo.user} />
														</div>
													)}
												</Dropdown>
											</div>
										</div>
									</div>
								</Header>
								<Content className="site-content">
									<Spin
										//Maximum ZIndex
										style={{ zIndex: 99999 }}
										size="large"
										tip={
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													justifyContent: "center",
													alignItems: "center",
												}}
											>
												{/* <span style={{ fontSize: "30px" }}>{ModelLoadDesc}</span> <Progress style={{ width: "50%" }} percent={ModelLoadPercent} /> */}
											</div>
										}
										indicator={
											<div
												style={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													top: 0,
													insetInlineStart: 0,
													height: "100%",
													width: "100%",
													margin: 0,
												}}
											>
												<img
													className={`no-visual-search ${localStorage.getItem("theme") === "light" ? "invert" : ""}`}
													style={{ position: "absolute", width: "10%" }}
													src={elementalLoadingLogo}
													alt=""
												/>
												<img
													className={`spin no-visual-search ${localStorage.getItem("theme") === "light" ? "invert" : ""}`}
													style={{ position: "absolute", width: "15%" }}
													src={elementalLoadingOutline}
													alt=""
												/>
											</div>
										}
										spinning={isLoading}
									>
										<ErrorBoundary
											resetKeys={[errorBoundaryKey]}
											fallbackRender={handleErrorFallback}
											onError={logError}
										>
											<Outlet />
										</ErrorBoundary>
									</Spin>
								</Content>
								<Footer className="site-footer">
									<button
										type="button"
										className="site-footer-version"
										title="What's new"
										onClick={() => setWhatsNewOpen(true)}
									>
										Version {file.version}
									</button>
									<Space size={15}>
										<span>&copy; {dayjs().year()} Elemental Energies</span>
										<Image
											className="no-visual-search"
											preview={false}
											width="50px"
											src={
												mode === "light"
													? terraLogoFooterLight
													: terraLogoFooterDark
											}
										/>
									</Space>
								</Footer>

								<WhatsNewDrawer
									open={isWhatsNewOpen}
									onClose={() => setWhatsNewOpen(false)}
								/>

								<LeaveWithChangesModal
									isFormUpdating={formUpdatingRef.current}
									setFormUpdating={handleFormUpdating}
									pageChange={pageChange}
								/>

								<ContextMenu
									position={contextMenuPosition}
									info={contextMenuInfo}
									currentTenantKey={currentTenantKey}
								/>
							</Layout>
						</Layout>
					</HomeContext.Provider>
				);
			}}
		</MainContext.Consumer>
	);
};

Home.propTypes = {
	userInfo: PropTypes.any,
	currentLocation: PropTypes.any,
	menu: PropTypes.any,
	tenants: PropTypes.any,
};

export default Home;
